import { Injectable } from '@angular/core'

import * as _ from 'lodash'

import { AuthStore } from '@api/common/store/auth'
import { ProfileDto, UserDto } from '@api/common/dto'

import { HttpService } from './http.service'

@Injectable()
export class AuthService {
  constructor(
    private httpService: HttpService,
    private authStore: AuthStore) {
  }

  async isAdmin() {
    const isAdmin = await this.httpService.get<boolean>('/admin/v1/is-admin')

    this.authStore.update({ isAdmin })
  }

  async isEmployer() {
    return await this.httpService.get<boolean>('/employer/v1/is-employer')
  }

  async current() {
    const user = await this.httpService.get<ProfileDto>('/v1/profile')
    const authUser = _.clone(user)
    
    this.authStore.update({ user: authUser, roles: authUser.roleNames })

    return user
  }

  async updatePassword(currentPassword: string, newPassword: string) {
    return await this.httpService.patch<boolean>('/auth/v1/update-password', {
      currentPassword,
      newPassword
    })
  }
  
  async sendEmailVerification(email: string) {
    return await this.httpService.post<boolean>('/auth/v1/email-verification', { email })
  }

  // Each dashboard signs in through its own route; /auth/v1/login belongs to the
  // job-seeker website and mobile app.
  async adminLogIn(email: string, password: string, shouldRemember: boolean) {
    return await this.logInWith('/admin/v1/auth/login', email, password, shouldRemember)
  }

  async employerLogIn(email: string, password: string, shouldRemember: boolean) {
    return await this.logInWith('/employer/v1/auth/login', email, password, shouldRemember)
  }

  async employerForgetPassword(email: string) {
    return await this.httpService.post<boolean>('/employer/v1/auth/forget-password', { email })
  }

  async getEmployerResetPassword(token: string) {
    return await this.httpService.get<UserDto>('/employer/v1/auth/reset-password/' + token)
  }

  async employerResetPassword(token: string, password: string) {
    return await this.httpService.patch<boolean>('/employer/v1/auth/reset-password', {token, newPassword: password})
  }

  async adminForgetPassword(email: string) {
    return await this.httpService.post<boolean>('/admin/v1/auth/forget-password', { email })
  }

  async getAdminResetPassword(token: string) {
    return await this.httpService.get<UserDto>('/admin/v1/auth/reset-password/' + token)
  }

  async adminResetPassword(token: string, password: string) {
    return await this.httpService.patch<boolean>('/admin/v1/auth/reset-password', {token, newPassword: password})
  }

  // Job-seeker password reset. The website and mobile app have no reset page, so
  // their reset emails link to this app's /user/reset-password.
  async getUserResetPassword(token: string) {
    return await this.httpService.get<UserDto>('/v1/reset-password/' + token)
  }

  async userResetPassword(token: string, password: string) {
    return await this.httpService.patch<boolean>('/auth/v1/reset-password', {token, newPassword: password})
  }

  async logOut() {
    await this.httpService.delete('/auth/v1/logout')

    return await this.httpService.removeSessionToken()
  }

  private async logInWith(path: string, email: string, password: string, shouldRemember: boolean) {
    const { sessionToken, user } = await this.httpService.post(path, {email, password})

    await this.httpService.setSessionToken(sessionToken, shouldRemember)

    return user
  }
}
